'use client';
import { IconEmail } from '@leland-dev/leland-ui-library';
import { type FC, useCallback, useState } from 'react';

import { type Scalars } from '../../__generated-gql-types__/globalTypes';
import TextInput from '../inputs/TextInput';

import {
  useBootcampGetNotifiedInputBootcampQuery,
  useBootcampGetNotifiedJoinDiscoveryListMutation,
  useBootcampGetNotifiedLeaveDiscoveryListMutation,
} from './__generated-gql-types__/BootcampGetNotifiedInput.generated';

interface BootcampGetNotifiedInputProps {
  bootcampUrn: Scalars['BootcampUrn']['output'];
  className?: string;
}

export const BootcampGetNotifiedInput: FC<BootcampGetNotifiedInputProps> = ({
  bootcampUrn,
  className,
}) => {
  const [joinedDiscoveryList, setJoinedDiscoveryList] = useState(false);
  useBootcampGetNotifiedInputBootcampQuery({
    variables: { bootcampUrn },
    onCompleted(data) {
      setJoinedDiscoveryList(data.bootcamp?.isOnDiscovery ?? false);
    },
  });

  const [email, setEmail] = useState('');
  const [joinDiscoveryList, { loading }] =
    useBootcampGetNotifiedJoinDiscoveryListMutation();
  const [leaveDiscoveryList, { loading: leaveLoading }] =
    useBootcampGetNotifiedLeaveDiscoveryListMutation();

  const onSubmit = useCallback(async () => {
    await joinDiscoveryList({
      variables: {
        bootcampUrn,
        email,
      },
    });
    setJoinedDiscoveryList(true);
    setEmail('');
  }, [joinDiscoveryList, bootcampUrn, email]);

  const onLeaveDiscoveryList = useCallback(async () => {
    await leaveDiscoveryList({
      variables: {
        bootcampUrn,
        email,
      },
    });
    setJoinedDiscoveryList(false);
  }, [leaveDiscoveryList, bootcampUrn, email]);

  return (
    <div className={`w-full ${className}`}>
      <div>
        {!joinedDiscoveryList ? (
          <>
            <div className="mb-2 flex items-center gap-1 text-sm font-medium uppercase tracking-wider">
              <IconEmail className="size-4" /> Get notified about future cohorts
            </div>
            <TextInput
              label=""
              placeholder="Enter your email..."
              value={email}
              onChangeValue={(value) => setEmail(value as string)}
              button={
                <button
                  id="bootcamp_page--add_to_discovery_list"
                  className="font-medium text-leland-primary"
                  onClick={onSubmit}
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit'}
                </button>
              }
            />
          </>
        ) : (
          <div className="flex items-center justify-between rounded-md bg-leland-primary-extra-light p-4 text-leland-dark-green">
            <div className="flex items-center gap-1.5">
              <IconEmail className="size-4" />
              <p className="font-medium">
                You will be notified about future cohorts.
              </p>
            </div>
            <button
              id="bootcamp_page--remove_from_discovery_list"
              className="font-medium underline"
              onClick={onLeaveDiscoveryList}
              disabled={leaveLoading}
            >
              {leaveLoading ? 'Undoing...' : 'Undo'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
